import React, { useState, useEffect, useCallback } from 'react';
import {
  Form, Input, Select, Switch, Button, Card, Row, Col, Tag,
  Table, Space, Typography, Radio, Modal, Divider, Badge, App, Tooltip,
} from 'antd';
import {
  ApiOutlined, SendOutlined, CheckCircleOutlined, CloseCircleOutlined,
  CodeOutlined, SaveOutlined, PlayCircleOutlined, EyeOutlined,
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const PRIMARY = '#2563eb';
const DARK    = '#1e293b';

const getToken = () => localStorage.getItem('rfid_token');
const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

const AVAILABLE_FIELDS = [
  'delegate_name', 'rfid_tag', 'category_name', 'company',
  'current_location', 'event_name', 'last_seen', 'reader_name',
  'rssi', 'antenna_port', 'scan_timestamp',
];

const EXAMPLE_DATA = {
  delegate_name:    'Jane Doe',
  rfid_tag:         'E2000017221101431820C4A6',
  category_name:    'Speaker',
  company:          'Acme Corp',
  current_location: 'Hall A - Gate 1',
  event_name:       'Tech Summit 2026',
  last_seen:        '2026-04-03T10:45:22Z',
  reader_name:      'Reader-01',
  rssi:             -62,
  antenna_port:     1,
  scan_timestamp:   '2026-04-03T10:45:22.341Z',
};

const STORAGE_KEY = 'client_push_configs';
const loadLocalConfigs = () => { try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); } catch { return []; } };
const saveLocalConfigs = (c) => localStorage.setItem(STORAGE_KEY, JSON.stringify(c));

const defaultFieldMapping = () =>
  AVAILABLE_FIELDS.reduce((acc, f) => { acc[f] = { enabled: true, customKey: '' }; return acc; }, {});

const logColumns = [
  { title: 'Timestamp',    dataIndex: 'timestamp',    width: 170, render: v => <Text style={{ fontSize: 12 }}>{v}</Text> },
  {
    title: 'Status', dataIndex: 'status', width: 110,
    render: v => {
      const ok = String(v).startsWith('2');
      return <Tag icon={ok ? <CheckCircleOutlined /> : <CloseCircleOutlined />} color={ok ? 'success' : 'error'}>{v}</Tag>;
    },
  },
  { title: 'Endpoint',     dataIndex: 'endpoint',     ellipsis: true, render: v => <Tooltip title={v}><Text style={{ fontSize: 12 }}>{v}</Text></Tooltip> },
  { title: 'Records',      dataIndex: 'records',      width: 80, align: 'center' },
  { title: 'Response (ms)', dataIndex: 'responseTime', width: 120, align: 'center', render: v => <Text>{v} ms</Text> },
];

export default function ClientAPIPush() {
  const { message: msg, modal } = App.useApp();
  const [form] = Form.useForm();

  const [configs, setConfigs]         = useState([]);
  const [activeConfigId, setActiveConfigId] = useState(null);
  const [saving, setSaving]           = useState(false);
  const [authType, setAuthType]       = useState('none');
  const [trigger, setTrigger]         = useState('live');
  const [fieldMapping, setFieldMapping] = useState(defaultFieldMapping());
  const [events, setEvents]           = useState([]);
  const [logs, setLogs]               = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult]   = useState(null);

  useEffect(() => {
    axios.get('/api/events', authHeaders())
      .then(res => setEvents(res.data?.data || []))
      .catch(() => setEvents([]));
  }, []);

  const fetchConfigs = useCallback(async () => {
    let apiConfigs = [];
    try {
      const res = await axios.get('/api/client-push/configs', authHeaders());
      apiConfigs = res.data?.data || [];
    } catch { /* fall through to localStorage */ }
    const local = loadLocalConfigs();
    const merged = [...apiConfigs];
    local.forEach(lc => { if (!merged.find(c => c.id === lc.id)) merged.push(lc); });
    setConfigs(merged);
  }, []);

  useEffect(() => { fetchConfigs(); }, [fetchConfigs]);

  const fetchLogs = useCallback(async () => {
    setLoadingLogs(true);
    try {
      const res = await axios.get('/api/client-push/logs', authHeaders());
      setLogs(res.data?.data || []);
    } catch {
      setLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  }, []);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const buildPreviewPayload = () => {
    const payload = {};
    AVAILABLE_FIELDS.forEach(f => {
      const m = fieldMapping[f];
      if (m?.enabled) payload[m.customKey?.trim() || f] = EXAMPLE_DATA[f];
    });
    return payload;
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = { ...values, fieldMapping, id: activeConfigId || undefined };
      let saved;
      if (activeConfigId) {
        const res = await axios.put(`/api/client-push/configs/${activeConfigId}`, payload, authHeaders()).catch(() => null);
        saved = res?.data || { ...payload };
      } else {
        const res = await axios.post('/api/client-push/configs', payload, authHeaders()).catch(() => null);
        saved = res?.data || { ...payload, id: Date.now() };
      }
      const local = loadLocalConfigs();
      const idx = local.findIndex(c => c.id === saved.id);
      if (idx >= 0) local[idx] = saved; else local.push(saved);
      saveLocalConfigs(local);
      await fetchConfigs();
      setActiveConfigId(saved.id);
      msg.success('Configuration saved successfully');
    } catch (err) {
      if (err?.errorFields) msg.error('Please fill in all required fields');
      else msg.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTestPush = async () => {
    try { await form.validateFields(['endpointUrl']); } catch {
      msg.error('Please enter an Endpoint URL before testing'); return;
    }
    setTestLoading(true);
    setTestModalOpen(true);
    setTestResult(null);
    const values = form.getFieldsValue();
    try {
      const res = await axios.post('/api/client-push/test', {
        endpointUrl:  values.endpointUrl,
        method:       values.method || 'POST',
        authType:     values.authType || 'none',
        authDetails:  { bearerToken: values.bearerToken, apiKeyHeader: values.apiKeyHeader, apiKeyValue: values.apiKeyValue, basicUsername: values.basicUsername, basicPassword: values.basicPassword },
        contentType:  values.contentType || 'application/json',
        payload:      buildPreviewPayload(),
      }, authHeaders());
      setTestResult({ success: true, data: res.data });
    } catch (err) {
      setTestResult({ success: false, data: err?.response?.data || { error: 'Request failed', message: err.message } });
    } finally {
      setTestLoading(false);
    }
  };

  const loadConfig = (cfg) => {
    setActiveConfigId(cfg.id);
    setAuthType(cfg.authType || 'none');
    setTrigger(cfg.trigger || 'live');
    setFieldMapping(cfg.fieldMapping || defaultFieldMapping());
    form.setFieldsValue({
      pushName: cfg.pushName, endpointUrl: cfg.endpointUrl,
      method: cfg.method || 'POST', authType: cfg.authType || 'none',
      bearerToken: cfg.bearerToken, apiKeyHeader: cfg.apiKeyHeader,
      apiKeyValue: cfg.apiKeyValue, basicUsername: cfg.basicUsername,
      basicPassword: cfg.basicPassword, contentType: cfg.contentType || 'application/json',
      trigger: cfg.trigger || 'live', intervalMinutes: cfg.intervalMinutes || 5,
      sourceModules: cfg.sourceModules || [], filterEvent: cfg.filterEvent,
    });
  };

  const handleNewConfig = () => {
    setActiveConfigId(null);
    setAuthType('none');
    setTrigger('live');
    setFieldMapping(defaultFieldMapping());
    form.resetFields();
  };

  const previewJson = JSON.stringify(buildPreviewPayload(), null, 2);

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <ApiOutlined style={{ fontSize: 28, color: PRIMARY, flexShrink: 0, marginTop: 4 }} />
        <div style={{ flex: 1 }}>
          <Title level={4} style={{ margin: 0, color: DARK }}>Client API Push</Title>
          <Text type="secondary" style={{ fontSize: 13 }}>
            Configure external API endpoints to push RFID / delegate data to client systems
          </Text>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {configs.length > 0 && (
            <Select placeholder="Load saved config…" style={{ width: 200 }} value={activeConfigId}
              allowClear onClear={handleNewConfig}
              onChange={val => { const cfg = configs.find(c => c.id === val); if (cfg) loadConfig(cfg); }}>
              {configs.map(c => (
                <Option key={c.id} value={c.id}>{c.pushName || c.endpointUrl || `Config #${c.id}`}</Option>
              ))}
            </Select>
          )}
          <Button onClick={handleNewConfig}>+ New Config</Button>
        </div>
      </div>

      <Row gutter={20}>
        {/* LEFT: Config Form */}
        <Col xs={24} lg={10}>
          <Card
            title={<Space><ApiOutlined style={{ color: PRIMARY }} /><span style={{ color: DARK, fontWeight: 600 }}>Push Configuration</span></Space>}
            style={{ borderRadius: 12 }}
            bodyStyle={{ paddingTop: 16 }}
          >
            <Form form={form} layout="vertical"
              initialValues={{ method: 'POST', authType: 'none', contentType: 'application/json', trigger: 'live', intervalMinutes: 5 }}>

              <Divider orientation="left"><Text strong style={{ color: PRIMARY }}>Endpoint Setup</Text></Divider>

              <Form.Item name="pushName" label="Push Name" rules={[{ required: true, message: 'Required' }]}>
                <Input placeholder="e.g. Client CRM Sync" />
              </Form.Item>
              <Form.Item name="endpointUrl" label="Endpoint URL"
                rules={[{ required: true, message: 'Required' }, { type: 'url', message: 'Enter a valid URL' }]}>
                <Input placeholder="https://client.example.com/api/rfid-data" />
              </Form.Item>
              <Form.Item name="method" label="HTTP Method">
                <Radio.Group buttonStyle="solid">
                  <Radio.Button value="POST">POST</Radio.Button>
                  <Radio.Button value="PUT">PUT</Radio.Button>
                  <Radio.Button value="PATCH">PATCH</Radio.Button>
                </Radio.Group>
              </Form.Item>
              <Form.Item name="authType" label="Auth Type">
                <Select onChange={val => setAuthType(val)}>
                  <Option value="none">None</Option>
                  <Option value="bearer">Bearer Token</Option>
                  <Option value="apikey">API Key</Option>
                  <Option value="basic">Basic Auth</Option>
                </Select>
              </Form.Item>
              {authType === 'bearer' && (
                <Form.Item name="bearerToken" label="Token" rules={[{ required: true, message: 'Required' }]}>
                  <Input.Password placeholder="Bearer token value" />
                </Form.Item>
              )}
              {authType === 'apikey' && (<>
                <Form.Item name="apiKeyHeader" label="Header Name" rules={[{ required: true, message: 'Required' }]}>
                  <Input placeholder="e.g. X-API-Key" />
                </Form.Item>
                <Form.Item name="apiKeyValue" label="Key Value" rules={[{ required: true, message: 'Required' }]}>
                  <Input.Password placeholder="API key value" />
                </Form.Item>
              </>)}
              {authType === 'basic' && (<>
                <Form.Item name="basicUsername" label="Username" rules={[{ required: true, message: 'Required' }]}>
                  <Input placeholder="Username" />
                </Form.Item>
                <Form.Item name="basicPassword" label="Password" rules={[{ required: true, message: 'Required' }]}>
                  <Input.Password placeholder="Password" />
                </Form.Item>
              </>)}
              <Form.Item name="contentType" label="Content-Type">
                <Radio.Group>
                  <Radio value="application/json">application/json</Radio>
                  <Radio value="application/x-www-form-urlencoded">form-urlencoded</Radio>
                </Radio.Group>
              </Form.Item>

              <Divider orientation="left"><Text strong style={{ color: PRIMARY }}>Data Source</Text></Divider>

              <Form.Item name="trigger" label="Trigger">
                <Select onChange={val => setTrigger(val)}>
                  <Option value="live">Live Scan (real-time, every scan)</Option>
                  <Option value="scheduled">Scheduled (every X minutes)</Option>
                  <Option value="ondemand">On-Demand</Option>
                </Select>
              </Form.Item>
              {trigger === 'scheduled' && (
                <Form.Item name="intervalMinutes" label="Interval">
                  <Select>
                    {[1, 5, 10, 15, 30, 60].map(m => (
                      <Option key={m} value={m}>Every {m} minute{m > 1 ? 's' : ''}</Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
              <Form.Item name="sourceModules" label="Source Module">
                <Select mode="multiple" placeholder="Select source modules">
                  <Option value="live_tracking">Live Tracking</Option>
                  <Option value="raw_inventory">Raw Inventory</Option>
                  <Option value="delegate_dashboard">Delegate Dashboard</Option>
                  <Option value="reports">Reports</Option>
                </Select>
              </Form.Item>
              <Form.Item name="filterEvent" label="Filter by Event">
                <Select allowClear placeholder="All events">
                  {events.map(e => <Option key={e.id} value={e.id}>{e.event_name}</Option>)}
                </Select>
              </Form.Item>

              <Divider orientation="left"><Text strong style={{ color: PRIMARY }}>Field Mapping</Text></Divider>

              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 12px', marginBottom: 16 }}>
                {AVAILABLE_FIELDS.map(field => {
                  const m = fieldMapping[field] || { enabled: true, customKey: '' };
                  return (
                    <div key={field} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                      <Switch size="small" checked={m.enabled} style={{ flexShrink: 0 }}
                        onChange={checked => setFieldMapping(prev => ({ ...prev, [field]: { ...prev[field], enabled: checked } }))} />
                      <Text code style={{ fontSize: 12, minWidth: 110, maxWidth: 140, color: m.enabled ? DARK : '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {field}
                      </Text>
                      <Text style={{ color: '#94a3b8', fontSize: 12, flexShrink: 0 }}>→</Text>
                      <Input size="small" placeholder={field} value={m.customKey} disabled={!m.enabled}
                        style={{ flex: 1, fontSize: 12 }}
                        onChange={e => setFieldMapping(prev => ({ ...prev, [field]: { ...prev[field], customKey: e.target.value } }))} />
                    </div>
                  );
                })}
              </div>

              <Space wrap style={{ width: '100%', justifyContent: 'flex-end' }}>
                <Button icon={<PlayCircleOutlined />} onClick={handleTestPush} style={{ borderColor: '#16a34a', color: '#16a34a' }}>
                  Test Push
                </Button>
                <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}
                  style={{ background: PRIMARY, borderColor: PRIMARY }}>
                  Save Configuration
                </Button>
              </Space>
            </Form>
          </Card>
        </Col>

        {/* RIGHT: Preview + Logs */}
        <Col xs={24} lg={14}>
          <Card
            title={<Space><EyeOutlined style={{ color: PRIMARY }} /><span style={{ color: DARK, fontWeight: 600 }}>Payload Preview</span><Tag color="blue">Live</Tag></Space>}
            style={{ borderRadius: 12, marginBottom: 16 }}
            bodyStyle={{ padding: 0 }}
          >
            <pre style={{
              background: '#0f172a', color: '#e2e8f0', margin: 0,
              padding: '16px 20px', borderRadius: '0 0 12px 12px',
              fontSize: 13, lineHeight: 1.6, overflowX: 'auto', maxHeight: 340, overflowY: 'auto',
            }}>
              <code>{previewJson}</code>
            </pre>
          </Card>

          <Card
            title={<Space><CodeOutlined style={{ color: PRIMARY }} /><span style={{ color: DARK, fontWeight: 600 }}>Recent Push Log</span><Badge count={logs.length} style={{ background: PRIMARY }} /></Space>}
            extra={
              <Space>
                <Button size="small" onClick={fetchLogs} loading={loadingLogs}>Refresh</Button>
                <Button size="small" danger onClick={() => modal.confirm({ title: 'Clear all logs?', onOk: () => setLogs([]) })}>Clear Log</Button>
              </Space>
            }
            style={{ borderRadius: 12 }}
            bodyStyle={{ padding: 0 }}
          >
            <Table
              columns={logColumns} dataSource={logs} rowKey={(r, i) => r.id || i}
              size="small" scroll={{ x: 600 }} pagination={{ pageSize: 8, size: 'small' }}
              locale={{ emptyText: (
                <div style={{ padding: 24, color: '#94a3b8', textAlign: 'center' }}>
                  <SendOutlined style={{ fontSize: 28, marginBottom: 8 }} /><br />
                  No push logs yet. Run a test or trigger a push to see logs here.
                </div>
              )}}
              style={{ borderRadius: '0 0 12px 12px' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Test Modal */}
      <Modal
        open={testModalOpen} onCancel={() => setTestModalOpen(false)}
        footer={<Button type="primary" onClick={() => setTestModalOpen(false)}>Close</Button>}
        title={<Space><SendOutlined style={{ color: PRIMARY }} /><span>Test Push Result</span></Space>}
        width={560}
      >
        {testLoading ? (
          <div style={{ textAlign: 'center', padding: '32px 0', color: '#64748b' }}>
            <SendOutlined style={{ fontSize: 32, marginBottom: 12 }} /><br />
            <Text type="secondary">Sending test payload…</Text>
          </div>
        ) : testResult ? (
          <div>
            <div style={{ marginBottom: 12 }}>
              {testResult.success
                ? <Tag icon={<CheckCircleOutlined />} color="success" style={{ fontSize: 14 }}>Success</Tag>
                : <Tag icon={<CloseCircleOutlined />} color="error" style={{ fontSize: 14 }}>Failed</Tag>}
            </div>
            <Paragraph style={{ marginBottom: 4 }}><Text strong>Response:</Text></Paragraph>
            <pre style={{ background: '#0f172a', color: '#e2e8f0', padding: 16, borderRadius: 8, fontSize: 12, maxHeight: 300, overflowY: 'auto' }}>
              {JSON.stringify(testResult.data, null, 2)}
            </pre>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
